export const waitForTimeout = (ms: number) => {
  return new Promise(resolve => {
    setTimeout(resolve, ms)
  })
}

export const waitFor = async <R>(
  cb: () => Promise<R>,
  opts: { timeout?: number; interval?: number } = {},
): Promise<R> => {
  const { timeout = 10000, interval = 1000 } = opts

  let elapsed = 0

  let value = await cb()
  while (!value) {
    await waitForTimeout(interval)
    elapsed += interval
    if (elapsed > timeout) {
      throw new Error('Timeout')
    }
    value = await cb()
  }
  return value
}

export const waitForArray = async <R>(
  size: number = 1,
  cb: () => Promise<R>,
  opts: { timeout?: number; interval?: number } = {},
): Promise<R> => {
  const current = await waitFor(() => cb(), opts)
  if (current && !Array.isArray(current)) {
    throw new Error(`Expected return value to be array in waitForArray. Is: ${typeof current}`)
  }
  if (Array.isArray(current) && current.length < size) {
    return waitForArray(size, cb, opts)
  }
  return current
}

export function asyncSingletonInitializer<Args extends never[], T>(
  cb: (...args: Args) => Promise<T>,
): (...args: Args) => Promise<T> {
  let promise: Promise<T> | undefined

  return (...args: Args) => {
    if (promise !== undefined) {
      return promise
    }

    promise = cb(...args)
    return promise
  }
}

export function asyncDedupe<Args extends never[], T>(fn: (...args: Args) => Promise<T>): (...args: Args) => Promise<T> {
  let inFlight: Promise<T> | null = null

  return async (...args: Args) => {
    if (await inFlight) {
      return inFlight
    }

    inFlight = fn(...args)

    try {
      return await inFlight
    } finally {
      inFlight = null
    }
  }
}
